
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import lodash from "lodash";
import { Control } from "@/types/controls";
import {
  getActiveElementOverrideVariables,
  getVariablePreviewValue,
  getVariables
} from "@/oxygen/variables/lib/utils";
import defensiveCopy from "@/util/defensive-copy";

lodash.templateSettings.interpolate = /{([\s\S]+?)}/g;

@Component
export default class RepeaterItemTitleText extends Vue {
  @Prop({
    default: ""
  })
  titleTemplate!:
    | string
    | ((row: Record<string, unknown> | undefined) => string);

  @Prop({
    default: "Item"
  })
  defaultTitle!: string;

  @Prop()
  value: Record<string, unknown> | undefined;

  @Prop({ default: () => [] })
  controls!: Control[];

  get title(): string {
    const cleanedTitle = this.parsedTitle.replace(/\s+/g, "");
    return cleanedTitle ? this.parsedTitle : this.defaultTitle;
  }

  get titleTemplateStr() {
    const template =
      typeof this.titleTemplate === "function"
        ? this.titleTemplate(this.value)
        : this.titleTemplate;

    // lodash.template does not support dashes or the "var" token.
    return template.replace(/{var-[^{}]*}/g, match => match.replace(/-/g, "_"));
  }

  get compiledTemplate() {
    return lodash.template(this.titleTemplateStr);
  }

  get parsedTitle() {
    const variables = { ...this.emptyVariables, ...this.filledVariables };
    return this.compiledTemplate(variables);
  }

  get variablesInTitle() {
    // Finds all mustache {variables} inside a string.
    const matches = this.titleTemplateStr.matchAll(/{(.*?)}/g);

    // Returns an array of capture groups
    return Array.from(matches, m => m[1]);
  }

  get emptyVariables() {
    // Turns ['foo', 'bar'] into {foo: null, bar: null}
    return this.variablesInTitle.reduce((ac, a) => ({ ...ac, [a]: null }), {});
  }

  get filledVariables(): Record<string, unknown> {
    const variables = defensiveCopy(this.value) || {};
    const labels = this.labelsFromDropdownItems;

    // Add CSS variables to the variables map, so the actual values are shown.
    const overrideVariables = getActiveElementOverrideVariables();
    const cssVariables = getVariables();
    cssVariables.forEach(variable => {
      const formattedKey = `var_${variable.id.replaceAll("-", "_")}`;
      const foundOverride = overrideVariables.find(v => v.id === variable.id);

      variables[formattedKey] = getVariablePreviewValue(
        variable,
        foundOverride
      );
    });

    // Swaps out variables with their labels if they exist
    if (labels) {
      return Object.keys(variables).reduce((acc, key) => {
        acc[key] = labels[key] || variables[key];
        return acc;
      }, {} as Record<string, unknown>);
    }

    return variables;
  }

  get labelsFromDropdownItems(): Record<string, string> {
    const labels: Record<string, string> = {};

    if (!this.value) {
      return labels;
    }

    Object.keys(this.value).forEach(key => {
      const control = this.controls.find(c => c.slug === key);

      if (control) {
        const selectedItem = control.options.items?.find(
          item => item.value === this.value![key]
        );

        if (selectedItem?.text) {
          labels[key] = selectedItem?.text;
        }
      }
    });

    return labels;
  }
}
